import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ShieldAlert, 
  ShieldCheck, 
  AlertTriangle, 
  Camera,
  Calendar,
  User,
  Home,
  Clock,
  Eye,
  RefreshCw,
  Info,
  XCircle,
  CheckCircle2,
  Smartphone,
  MapPin,
  Shield,
  ShieldOff,
  ChevronDown,
  ChevronUp,
  Star,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { getPhotoUrl } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurveillanceDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('photos');
  
  // Photos state
  const [photos, setPhotos] = useState([]);
  const [photoStats, setPhotoStats] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // FDM state
  const [suspiciousFdm, setSuspiciousFdm] = useState(null);
  const [loadingFdm, setLoadingFdm] = useState(true);
  const [expandedFdm, setExpandedFdm] = useState({});

  // ===== PHOTOS FUNCTIONS =====
  const fetchPhotosData = useCallback(async () => {
    setLoadingPhotos(true);
    try {
      const [photosRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/photos/suspect?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/photos/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (photosRes.ok) {
        const photosData = await photosRes.json();
        setPhotos(photosData);
      }
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setPhotoStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching photos data:', error);
      toast.error('Erreur lors du chargement des photos');
    } finally {
      setLoadingPhotos(false);
    }
  }, [token]);

  // ===== FDM FUNCTIONS =====
  const fetchFdmData = useCallback(async () => {
    setLoadingFdm(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/suspicious-fdm`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuspiciousFdm(data);
      }
    } catch (error) {
      console.error('Error fetching FDM data:', error);
      toast.error('Erreur lors du chargement des données FDM');
    } finally {
      setLoadingFdm(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user || !['admin', 'technician'].includes(user.role)) {
      navigate('/login');
      return;
    }
    fetchPhotosData();
    fetchFdmData();
  }, [user, navigate, fetchPhotosData, fetchFdmData]);

  const getVerdictBadge = (verification) => {
    if (!verification) {
      return <Badge className="bg-gray-100 text-gray-600">Non vérifié</Badge>;
    }
    
    const { verdict, trustScore } = verification;
    
    if (verdict === 'suspect') {
      return (
        <Badge className="bg-red-100 text-red-700 border border-red-300">
          <XCircle className="h-3 w-3 mr-1" />
          Suspect ({trustScore}%)
        </Badge>
      );
    }
    if (verdict === 'warning') {
      return (
        <Badge className="bg-orange-100 text-orange-700 border border-orange-300">
          <AlertTriangle className="h-3 w-3 mr-1" />
          À vérifier ({trustScore}%)
        </Badge>
      );
    }
    return (
      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        OK ({trustScore}%)
      </Badge>
    );
  };

  const toggleExpand = (fdmId) => {
    setExpandedFdm(prev => ({ ...prev, [fdmId]: !prev[fdmId] }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'short_duration': return <Clock className="h-4 w-4" />;
      case 'gps_failed': return <MapPin className="h-4 w-4" />;
      case 'non_authentic_photos': return <Camera className="h-4 w-4" />;
      case 'low_quality_scores': return <Star className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return <ShieldOff className="h-6 w-6 text-red-600" />;
    if (score >= 50) return <ShieldAlert className="h-6 w-6 text-orange-500" />;
    return <Shield className="h-6 w-6 text-yellow-500" />;
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Critique';
    if (score >= 50) return 'Élevé';
    return 'Modéré';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const refreshAll = () => {
    fetchPhotosData();
    fetchFdmData();
  };

  // Compteurs pour les badges des onglets
  const suspectPhotosCount = photos.filter(p => p.verification?.verdict === 'suspect').length;
  const suspiciousFdmCount = suspiciousFdm?.total_suspicious || 0;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      
      <main className="flex-1 lg:ml-72">
        <div className="p-4 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#003366] flex items-center gap-2">
                <ShieldAlert className="h-7 w-7" />
                Surveillance Anti-Fraude
              </h1>
              <p className="text-gray-500 mt-1">Détection des anomalies et comportements suspects</p>
            </div>
            <Button onClick={refreshAll} variant="outline" className="gap-2">
              <RefreshCw className={`h-4 w-4 ${(loadingPhotos || loadingFdm) ? 'animate-spin' : ''}`} />
              Actualiser tout
            </Button>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 h-12">
              <TabsTrigger 
                value="photos" 
                className="flex items-center gap-2 data-[state=active]:bg-red-50 data-[state=active]:text-red-700"
              >
                <Camera className="h-4 w-4" />
                Photos Suspectes
                {suspectPhotosCount > 0 && (
                  <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5">
                    {suspectPhotosCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="fdm" 
                className="flex items-center gap-2 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700"
              >
                <Users className="h-4 w-4" />
                FDM Suspectes
                {suspiciousFdmCount > 0 && (
                  <Badge className="ml-1 bg-orange-500 text-white text-xs px-1.5 py-0.5">
                    {suspiciousFdmCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ==================== PHOTOS TAB ==================== */}
            <TabsContent value="photos" className="mt-0">
              {/* Photo Stats Cards */}
              {photoStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Camera className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{photoStats.total}</p>
                          <p className="text-xs text-gray-500">Total photos</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100">
                          <ShieldCheck className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{photoStats.ok}</p>
                          <p className="text-xs text-gray-500">Vérifiées OK</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-orange-100">
                          <AlertTriangle className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{photoStats.warning}</p>
                          <p className="text-xs text-gray-500">À vérifier</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-100">
                          <XCircle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{photoStats.suspect}</p>
                          <p className="text-xs text-gray-500">Suspectes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gray-100">
                          <Info className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{photoStats.not_verified}</p>
                          <p className="text-xs text-gray-500">Non vérifiées</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Photos List */}
              {loadingPhotos ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : photos.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-8 text-center">
                    <ShieldCheck className="h-12 w-12 mx-auto text-emerald-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700">Aucune photo suspecte</h3>
                    <p className="text-gray-500 mt-2">Toutes les photos vérifiées sont conformes.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {photos.map((photo) => (
                    <Card key={photo.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          {/* Thumbnail */}
                          <div 
                            className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 cursor-pointer flex-shrink-0"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img 
                              src={getPhotoUrl(photo.storage_path, token)} 
                              alt="Photo" 
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.src = '/placeholder.png'; }}
                            />
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <h3 className="font-semibold text-gray-900 truncate">
                                  {photo.property_name || 'Logement inconnu'}
                                </h3>
                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {photo.uploaded_by_name || 'Inconnu'}
                                </p>
                              </div>
                              {getVerdictBadge(photo.verification)}
                            </div>
                            
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(photo.uploaded_at)}
                              </span>
                              {photo.verification?.deviceInfo && (
                                <span className="flex items-center gap-1">
                                  <Smartphone className="h-3 w-3" />
                                  {photo.verification.deviceInfo.model || 'Appareil inconnu'}
                                </span>
                              )}
                              {photo.gps_validation && (
                                <span className={`flex items-center gap-1 ${photo.gps_validation.validated ? 'text-emerald-600' : 'text-red-600'}`}>
                                  <MapPin className="h-3 w-3" />
                                  {photo.gps_validation.validated ? 'GPS OK' : `GPS ${Math.round(photo.gps_validation.distance_meters)}m`}
                                </span>
                              )}
                            </div>

                            {/* Warnings */}
                            {photo.verification?.flags && photo.verification.flags.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {photo.verification.flags.map((flag, idx) => (
                                  <Badge key={`${photo.id || photo.path || idx}-flag-${flag}`} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                                    {flag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex-shrink-0">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/tasks/${photo.task_id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ==================== FDM TAB ==================== */}
            <TabsContent value="fdm" className="mt-0">
              {/* FDM Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-full">
                      <ShieldOff className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-800">
                        {suspiciousFdm?.suspicious_fdm?.filter(f => f.alert_score >= 80).length || 0}
                      </p>
                      <p className="text-sm text-red-600">Critiques</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-full">
                      <ShieldAlert className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-800">
                        {suspiciousFdm?.suspicious_fdm?.filter(f => f.alert_score >= 50 && f.alert_score < 80).length || 0}
                      </p>
                      <p className="text-sm text-orange-600">Élevés</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-full">
                      <Shield className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-800">
                        {suspiciousFdm?.suspicious_fdm?.filter(f => f.alert_score < 50).length || 0}
                      </p>
                      <p className="text-sm text-yellow-600">Modérés</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <Camera className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-800">
                        {suspiciousFdm?.suspicious_fdm?.reduce((sum, f) => sum + (f.stats?.non_authentic_photos_count || 0), 0) || 0}
                      </p>
                      <p className="text-sm text-blue-600">Screenshots</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* FDM List */}
              {loadingFdm ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : suspiciousFdm?.suspicious_fdm?.length === 0 ? (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-8 text-center">
                    <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-green-800">Aucune alerte</h3>
                    <p className="text-green-600 mt-2">Toutes les FDM sont conformes sur les 30 derniers jours.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {suspiciousFdm?.suspicious_fdm?.map((fdm) => (
                    <Card 
                      key={fdm.id} 
                      className={`border-l-4 ${fdm.alert_score >= 80 ? 'border-l-red-500' : fdm.alert_score >= 50 ? 'border-l-orange-500' : 'border-l-yellow-500'}`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-3">
                            {getScoreIcon(fdm.alert_score)}
                            <div>
                              <CardTitle className="text-lg">{fdm.name}</CardTitle>
                              <p className="text-sm text-slate-500">{fdm.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              fdm.alert_score >= 80 ? 'bg-red-100 text-red-800' : 
                              fdm.alert_score >= 50 ? 'bg-orange-100 text-orange-800' : 
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              Score: {fdm.alert_score} - {getScoreLabel(fdm.alert_score)}
                            </span>
                            {fdm.require_gps_photos && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                GPS strict
                              </span>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        {/* Alerts */}
                        <div className="space-y-2 mb-4">
                          {fdm.alerts.map((alert, idx) => (
                            <div 
                              key={`${fdm.id}-alert-${alert.type || idx}`}
                              className={`flex items-center gap-2 p-2 rounded-lg border ${getSeverityColor(alert.severity)}`}
                            >
                              {getAlertIcon(alert.type)}
                              <span className="font-medium">{alert.message}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Stats Summary */}
                        <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {fdm.stats?.total_tasks_30d || 0} tâches (30j)
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {fdm.stats?.gps_failed_count || 0} hors GPS
                          </span>
                          <span className="flex items-center gap-1">
                            <Camera className="h-4 w-4" />
                            {fdm.stats?.non_authentic_photos_count || 0} screenshots
                          </span>
                          {fdm.stats?.avg_quality_score && (
                            <span className="flex items-center gap-1">
                              <Star className="h-4 w-4" />
                              Note moy: {fdm.stats.avg_quality_score}/10
                            </span>
                          )}
                        </div>
                        
                        {/* Expand/Collapse Details */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpand(fdm.id)}
                          className="w-full text-slate-600"
                        >
                          {expandedFdm[fdm.id] ? (
                            <>
                              <ChevronUp className="h-4 w-4 mr-2" />
                              Masquer les détails
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-4 w-4 mr-2" />
                              Voir les détails
                            </>
                          )}
                        </Button>
                        
                        {/* Expanded Details */}
                        {expandedFdm[fdm.id] && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            {/* Short duration tasks */}
                            {fdm.alerts.find(a => a.type === 'short_duration')?.details && (
                              <div className="mb-4">
                                <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  Prestations trop rapides
                                </h4>
                                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                                  {fdm.alerts.find(a => a.type === 'short_duration').details.map((task, idx) => (
                                    <div key={task.task_id || `dur-${idx}`} className="flex justify-between items-center text-sm">
                                      <span className="text-slate-600">{task.property_name}</span>
                                      <span className={`font-medium ${task.duration_minutes < 30 ? 'text-red-600' : 'text-orange-600'}`}>
                                        {task.duration_minutes} min
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex gap-2 mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/utilisateurs/${fdm.id}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Voir le profil
                              </Button>
                              {!fdm.require_gps_photos && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={async () => {
                                    try {
                                      const res = await fetch(`${API_URL}/api/users/${fdm.id}`, {
                                        method: 'PUT',
                                        headers: {
                                          'Authorization': `Bearer ${token}`,
                                          'Content-Type': 'application/json'
                                        },
                                        body: JSON.stringify({ require_gps_photos: true })
                                      });
                                      if (res.ok) {
                                        toast.success('GPS strict activé pour cette FDM');
                                        fetchFdmData();
                                      }
                                    } catch (e) {
                                      toast.error('Erreur');
                                    }
                                  }}
                                >
                                  <MapPin className="h-4 w-4 mr-2" />
                                  Activer GPS strict
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Analysis Period Info */}
              <p className="text-center text-sm text-gray-500 mt-6">
                Analyse sur les {suspiciousFdm?.analysis_period || '30 derniers jours'}
              </p>
            </TabsContent>
          </Tabs>
        </div>

        {/* Photo Preview Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
              <img 
                src={getPhotoUrl(selectedPhoto.storage_path, token)} 
                alt="Photo suspecte" 
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <Button 
                className="absolute top-2 right-2" 
                variant="secondary"
                onClick={() => setSelectedPhoto(null)}
              >
                <XCircle className="h-5 w-5" />
              </Button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4 rounded-b-lg">
                <p className="font-semibold">{selectedPhoto.property_name}</p>
                <p className="text-sm opacity-80">{selectedPhoto.uploaded_by_name} - {formatDate(selectedPhoto.uploaded_at)}</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SurveillanceDashboard;
